import config from 'services/config'
import { getDirectfulUploadHeaders } from 'services/helpers/responseHandler'

export function resolveFilePondUploadFile(file) {
  if (file instanceof Blob) {
    return file
  }

  if (file?.file instanceof Blob) {
    return file.file
  }

  return null
}

export function normalizeWidgetFileUrl(url) {
  const trimmed = `${url || ''}`.trim()

  if (!trimmed) {
    return ''
  }

  try {
    return decodeURIComponent(trimmed)
  } catch {
    return trimmed
  }
}

export function findNewWidgetFileUrl(knownUrls, candidateUrls) {
  const known = new Set((knownUrls || []).map(normalizeWidgetFileUrl).filter(Boolean))
  const candidates = (candidateUrls || []).map(url => `${url}`.trim()).filter(Boolean)
  const newOnes = candidates.filter(url => !known.has(normalizeWidgetFileUrl(url)))

  return newOnes[newOnes.length - 1] || ''
}

export function widgetUploadNeedsListFallback(parsedResponse) {
  return Boolean(parsedResponse?.isSuccess && !parsedResponse?.url)
}

export function createWidgetFileUploadProcess({
  kind,
  onUploadStart,
  onLoad,
  onError,
  apiUrl = config.apiUrl,
  fieldName = 'file'
}) {
  return (name, file, _metadata, load, error, progress) => {
    const uploadFile = resolveFilePondUploadFile(file)

    if (!uploadFile) {
      error('File missing')
      return { abort: () => {} }
    }

    if (onUploadStart) {
      onUploadStart()
    }

    const formData = new FormData()
    formData.append(fieldName || name || 'file', uploadFile, uploadFile.name || 'upload.png')
    formData.append('kind', kind)

    const request = new XMLHttpRequest()
    const uploadPath = 'widgets/files'
    const uploadHeaders = getDirectfulUploadHeaders(uploadPath)
    const endpoint = `${apiUrl.replace(/\/$/, '')}/${uploadPath}`

    request.open('POST', endpoint)

    Object.entries(uploadHeaders).forEach(([key, value]) => {
      if (value != null && value !== '') {
        request.setRequestHeader(key, value)
      }
    })

    request.upload.onprogress = event => {
      progress(event.lengthComputable, event.loaded, event.total)
    }

    request.onload = () => {
      if (request.status < 200 || request.status >= 300) {
        if (onError) {
          onError(request.responseText)
        }
        error(request.responseText || 'Upload failed')
        return
      }

      const response = request.responseText
      if (onLoad) {
        onLoad(response)
      }
      load(response)
    }

    request.onerror = () => {
      if (onError) {
        onError('Upload failed')
      }
      error('Upload failed')
    }

    request.send(formData)

    return {
      abort: () => {
        request.abort()
      }
    }
  }
}

export function prepareWidgetUploadFormData(formData, kind, fieldName = 'file') {
  if (!(formData instanceof FormData)) {
    return formData
  }

  const entries = [...formData.entries()]
  const hasMetadataFilePart = entries.some(
    ([key, value]) => key === fieldName && typeof value === 'string'
  )

  if (!hasMetadataFilePart) {
    appendWidgetUploadKind(formData, kind)
    return formData
  }

  const clean = new FormData()

  for (const [key, value] of entries) {
    if (key === fieldName) {
      continue
    }

    clean.append(key, value)
  }

  for (const [key, value] of entries) {
    if (key !== fieldName || typeof value === 'string') {
      continue
    }

    if (value instanceof Blob && value.size > 0) {
      clean.append(fieldName, value, value.name || 'upload.png')
      break
    }
  }

  appendWidgetUploadKind(clean, kind)
  return clean
}

export function sanitizeFilePondUploadFormData(formData, fieldName = 'file') {
  if (!(formData instanceof FormData)) {
    return formData
  }

  const sanitized = new FormData()
  let uploadFile = null
  let uploadFileName = 'upload.png'

  for (const [key, value] of formData.entries()) {
    if (key !== fieldName) {
      sanitized.append(key, value)
      continue
    }

    if (!(value instanceof Blob) || value.size <= 0) {
      continue
    }

    if (!uploadFile || value.size >= uploadFile.size) {
      uploadFile = value
      uploadFileName = value.name || uploadFileName
    }
  }

  if (!uploadFile) {
    return formData
  }

  sanitized.append(fieldName, uploadFile, uploadFileName)
  return sanitized
}

export function appendWidgetUploadKind(formData, kind) {
  if (!(formData instanceof FormData) || !kind) {
    return formData
  }

  formData.delete('kind')
  formData.append('kind', kind)
  return formData
}

export function getEmailImageUploadConfig(property) {
  if (property.includes('logo')) {
    return {
      kind: 'Logo',
      minWidth: 100,
      minHeight: 40
    }
  }

  return {
    kind: 'Background',
    minWidth: 200,
    minHeight: 200
  }
}

export function looksLikeHttpImageUrl(value) {
  return /^https?:\/\/.+/i.test(`${value || ''}`.trim())
}

export function normalizeUploadedImageUrl(value) {
  if (typeof value === 'string') {
    return value.trim()
  }

  if (value && typeof value.url === 'string') {
    return value.url.trim()
  }

  if (value && typeof value.Url === 'string') {
    return value.Url.trim()
  }

  return ''
}

export function parseWidgetUploadResponse(rawResponse) {
  if (rawResponse == null || rawResponse === '') {
    return { isSuccess: false, url: '' }
  }

  let data = rawResponse

  if (typeof rawResponse === 'string') {
    const trimmed = rawResponse.trim()

    if (!trimmed) {
      return { isSuccess: false, url: '' }
    }

    if (looksLikeHttpImageUrl(trimmed)) {
      return { isSuccess: true, url: trimmed }
    }

    try {
      data = JSON.parse(trimmed)
    } catch {
      return { isSuccess: false, url: '' }
    }

    if (typeof data === 'string') {
      const parsedUrl = data.trim()
      return looksLikeHttpImageUrl(parsedUrl)
        ? { isSuccess: true, url: parsedUrl }
        : { isSuccess: false, url: '' }
    }
  }

  const explicitSuccess = data?.isSuccess ?? data?.IsSuccess
  const url =
    normalizeUploadedImageUrl(data?.result ?? data?.Result) ||
    normalizeUploadedImageUrl(data?.url ?? data?.Url) ||
    normalizeUploadedImageUrl(data?.data ?? data?.Data)

  const messageUrl = normalizeUploadedImageUrl(data?.message ?? data?.Message)
  const resolvedUrl = url || (looksLikeHttpImageUrl(messageUrl) ? messageUrl : '')

  if (resolvedUrl) {
    return {
      isSuccess: explicitSuccess !== false,
      url: resolvedUrl,
      needsListFallback: false
    }
  }

  return {
    isSuccess: Boolean(explicitSuccess),
    url: '',
    needsListFallback: Boolean(explicitSuccess) && !resolvedUrl
  }
}
